/** Comma-delimited CC email lists for POs and vendor invoices. No count cap. */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isCcEmailFormat(raw: string): boolean {
  const e = raw.trim();
  if (!e) return false;
  return EMAIL_RE.test(e);
}

export function normalizeCcEmail(raw: string): string {
  return raw.trim().toLowerCase();
}

export function formatCcEmailList(emails: readonly string[]): string {
  return emails.map((e) => e.trim()).filter(Boolean).join(", ");
}

export function ccEmailListsEqual(a: readonly string[], b: readonly string[]): boolean {
  return formatCcEmailList(a) === formatCcEmailList(b);
}

/** Split on comma, semicolon, or whitespace; keep only non-empty tokens. */
export function splitCcEmailTokens(raw: string): string[] {
  return raw
    .split(/[,;\s]+/)
    .map((t) => t.trim())
    .filter(Boolean);
}

export function parseCcEmailList(raw: unknown):
  | { ok: true; emails: string[] }
  | { ok: false; error: string } {
  if (raw == null) return { ok: true, emails: [] };
  let tokens: string[] = [];
  if (Array.isArray(raw)) {
    for (const item of raw) {
      if (item == null || item === "") continue;
      if (typeof item !== "string") {
        return { ok: false, error: "CC emails must be strings." };
      }
      tokens.push(item);
    }
  } else if (typeof raw === "string") {
    tokens = splitCcEmailTokens(raw);
  } else {
    return { ok: false, error: "CC emails must be a list or comma-delimited string." };
  }
  const out: string[] = [];
  const seen = new Set<string>();
  for (const token of tokens) {
    if (!isCcEmailFormat(token)) {
      return { ok: false, error: `"${token.trim()}" is not a valid email.` };
    }
    const email = normalizeCcEmail(token);
    if (seen.has(email)) continue;
    seen.add(email);
    out.push(email);
  }
  return { ok: true, emails: out };
}

/** `undefined` keeps fallback (create/settings seed); otherwise parse strictly. */
export function resolveCcEmailsField(
  raw: unknown,
  fallback: string[]
): { ok: true; emails: string[] } | { ok: false; error: string } {
  if (raw === undefined) return { ok: true, emails: fallback };
  return parseCcEmailList(raw);
}

/** Commit one typed token (Enter / comma). Empty is a no-op. */
export function commitCcEmailToken(
  current: readonly string[],
  token: string
): { ok: true; emails: string[]; added: boolean } | { ok: false; error: string } {
  const trimmed = token.trim();
  if (!trimmed) return { ok: true, emails: [...current], added: false };
  const parsed = parseCcEmailList([trimmed]);
  if (!parsed.ok) return parsed;
  const email = parsed.emails[0];
  if (current.some((e) => normalizeCcEmail(e) === email)) {
    return { ok: true, emails: [...current], added: false };
  }
  return { ok: true, emails: [...current, email], added: true };
}

export function commitCcEmailTokens(
  current: readonly string[],
  raw: string
): { ok: true; emails: string[] } | { ok: false; error: string } {
  const tokens = splitCcEmailTokens(raw);
  if (tokens.length === 0) return { ok: true, emails: [...current] };
  let next = [...current];
  for (const token of tokens) {
    const r = commitCcEmailToken(next, token);
    if (!r.ok) return r;
    next = r.emails;
  }
  return { ok: true, emails: next };
}

export function mergeCcEmails(current: readonly string[], extras: readonly string[]): string[] {
  let next = [...current];
  for (const extra of extras) {
    const r = commitCcEmailToken(next, extra);
    if (r.ok) next = r.emails;
  }
  return next;
}
